import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { LocaleProvider } from './contexts/LocaleContext';
import { BrokerProvider } from './contexts/BrokerContext';
import { SubscriptionProvider } from './contexts/SubscriptionContext';

import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Instruments from './pages/Instruments';
import Clients from './pages/Clients';
import Accounts from './pages/Accounts';
import Trading from './pages/Trading';
import Reports from './pages/Reports';
import Settings from './pages/Settings';
import AccountGroups from './pages/AccountGroups';
import BrokerDepositsPage from './pages/BrokerDepositsPage';
import BrokerWithdrawalsPage from './pages/BrokerWithdrawalsPage';
import BrokerAuditLogPage from './pages/BrokerAuditLogPage';
import BrokerPluginsPage from './pages/BrokerPluginsPage';
import BrokerKycDashboard from './pages/BrokerKycDashboard';
import UpgradePage from './pages/UpgradePage';
import ApiDocsPage from './pages/ApiDocsPage';
import CascadeSettings from './pages/cascadeSettings';
import PluginDetailPage from './pages/PluginDetailPage';
import AdvancedAnalyticsPage from './pages/AdvancedAnalyticsPage'; // ✅ Already imported
import NotificationSettings from './pages/NotificationSettings';
import Layout from './components/Layout/Layout';
import OnboardingModal from './components/onboarding/OnboardingModal';
import SubscriptionGate from './components/subscription/SubscriptionGate';

function ProtectedRoute({ children }) {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--bg-primary)]">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-[var(--accent-primary)] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-[var(--text-secondary)]">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) return <Navigate to="/login" replace />;
  return children;
}

function AppRoutes() {
  const { isAuthenticated, onboardingCompleted, completeOnboarding } = useAuth();

  return (
    <>
      {isAuthenticated && !onboardingCompleted && (
        <OnboardingModal onComplete={completeOnboarding} />
      )}

      <Routes>
        <Route path="/login" element={<Login />} />

        <Route path="/upgrade" element={
          <ProtectedRoute>
            <UpgradePage />
          </ProtectedRoute>
        } />

        {/* Protected routes with Layout */}
        <Route path="/" element={
          <ProtectedRoute>
            <SubscriptionGate>
              <Layout />
            </SubscriptionGate>
          </ProtectedRoute>
        }>
          <Route index element={<Dashboard />} />
          <Route path="instruments" element={<Instruments />} />
          <Route path="clients" element={<Clients />} />
          <Route path="accounts" element={<Accounts />} />
          <Route path="account-groups" element={<AccountGroups />} />
          <Route path="cascade-settings" element={<CascadeSettings />} />
          <Route path="trading" element={<Trading />} />
          <Route path="reports" element={<Reports />} />
          <Route path="plugins" element={<BrokerPluginsPage />} />
          <Route path="plugins/:pluginKey" element={<PluginDetailPage />} />
          <Route path="analytics" element={<AdvancedAnalyticsPage />} /> {/* ✅ ADD THIS LINE */}
          <Route path="/broker/kyc" element={<BrokerKycDashboard />} />
          <Route path="deposits" element={<BrokerDepositsPage />} />
          <Route path="withdrawals" element={<BrokerWithdrawalsPage />} />
          <Route path="audit-log" element={<BrokerAuditLogPage />} />
          <Route path="/notifications" element={<NotificationSettings />} />
          <Route path="settings" element={<Settings />} />
          <Route path="api-docs" element={<ApiDocsPage />} />
        </Route>

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </>
  );
}

function App() {
  return (
    <BrowserRouter>
      <ThemeProvider>
        <LocaleProvider>
          <AuthProvider>
            <BrokerProvider>
              <SubscriptionProvider>
                <AppRoutes />
                <Toaster
                  position="top-right"
                  toastOptions={{
                    duration: 3000,
                    style: {
                      background: 'var(--bg-secondary)',
                      color: 'var(--text-primary)',
                      border: '1px solid var(--border-color)',
                    },
                    success: { iconTheme: { primary: 'var(--accent-success)', secondary: 'white' } },
                    error: { iconTheme: { primary: 'var(--accent-danger)', secondary: 'white' } },
                  }}
                />
              </SubscriptionProvider>
            </BrokerProvider>
          </AuthProvider>
        </LocaleProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
}

export default App;